import { useNavigate, useParams, useLocation } from "react-router-dom";
import { X } from "lucide-react";
import PropertyPage from "@/pages/PropertyPage";
import { useLockBodyScroll } from "@/hooks/useLockBodyScroll";

/**
 * PropertyModal — renders the PropertyPage inside a full-screen modal overlay.
 *
 * It is triggered by the React Router "background location" pattern: listing
 * pages navigate to `/property/:id` with `state={{ background: location }}` so
 * the listing remains rendered behind the modal. Closing the modal navigates
 * back, preserving scroll position.
 */
export default function PropertyModal() {
  useLockBodyScroll(true);
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const close = () => {
    // If we arrived here via a background-location navigation, history has a
    // sensible previous entry to go back to. Otherwise (direct deep link), we
    // fall back to /properties so the user is never stranded on a blank page.
    if (location.state?.background) {
      navigate(-1);
    } else {
      navigate("/properties");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm overflow-y-auto"
      data-testid="property-modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div
        className="relative mx-auto my-3 w-[96vw] max-w-7xl bg-background rounded-md shadow-2xl"
        data-testid="property-modal"
        role="dialog"
        aria-modal="true"
      >
        <button
          type="button"
          onClick={close}
          className="sticky top-2 float-right z-[2] mr-2 mt-2 inline-flex items-center justify-center h-9 w-9 rounded-full bg-background border border-border hover:bg-muted transition-colors"
          aria-label="Close property"
          data-testid="property-modal-close"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="px-0 sm:px-1 md:px-2 pb-2">
          <PropertyPage propertyId={id} isModal />
        </div>
      </div>
    </div>
  );
}
